import { useState } from 'react';
import { Badge } from '@repo/ui/src/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { useTranslation } from 'react-i18next';
import { FilePlus, Lock, Pencil, Trash } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { Client } from '@repo/shared-types';
import { Tags } from '../../../components/Tags';
import { useUpdateClient, useDeleteClient } from '../../../hooks/useClients';
import { useCan } from '../../../hooks/useCan';
import { useExtrasAccess } from '../../../hooks/useExtrasAccess';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';
import { toast } from 'sonner';
import { PageHeader } from '../../../components/PageHeader';

export function ClientHeader({ client, onEdit }: { client: Client; onEdit: () => void }) {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  const { mutate: updateClient } = useUpdateClient(client.id);
  const { mutate: deleteClient, isPending: isDeleting } = useDeleteClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canManage = useCan('manage:clients');
  // Extras three-state (see useExtrasAccess): enabled -> a real link;
  // !planAllows -> lock icon opening the upgrade dialog; planAllows &&
  // !orgSells -> hidden, because that is the org's own choice, not an upsell.
  const { planAllows, enabled: extrasEnabled } = useExtrasAccess();
  const openUpgradeDialog = useUpgradeDialogStore((s) => s.open);

  const handleAddTag = (tag: string) => {
    if (client.tags.includes(tag)) return;
    updateClient({ tags: [...client.tags, tag] });
  };

  const handleRemoveTag = (tag: string) => {
    updateClient({ tags: client.tags.filter((existing) => existing !== tag) });
  };

  const handleDelete = () => {
    deleteClient(client.id, {
      onSuccess: () => {
        toast.success(t('detail.delete'));
        navigate('/clients');
      },
    });
  };

  return (
    <>
      {/* No `size`: this component renders inside ClientDetailPage's own
          max-w-4xl container, which already does the centering/width job. */}
      <PageHeader
        back={{ label: t('detail.backToList'), onClick: () => navigate(-1) }}
        title={client.name}
        badge={<Badge variant="muted">{t('detail.identityBadge')}</Badge>}
        actions={[
          extrasEnabled
            ? {
                key: 'create-invoice',
                label: t('detail.createInvoice'),
                icon: FilePlus,
                to: `/invoices/new?clientId=${client.id}`,
              }
            : {
                key: 'create-invoice',
                label: t('detail.createInvoice'),
                icon: Lock,
                onClick: () => openUpgradeDialog('extras'),
                hidden: planAllows,
              },
          // Switches to the Thông tin tab rather than opening a dialog — the
          // edit form already lives there, and two edit surfaces is one too
          // many (13 §3). It is a navigation, not a mutation, so it carries no
          // permission gate: InfoTab is what disables its own fields for a
          // viewer without manage:clients.
          { key: 'edit', label: t('detail.edit'), icon: Pencil, variant: 'outline', onClick: onEdit },
          {
            key: 'delete',
            label: t('detail.delete'),
            icon: Trash,
            onClick: () => setConfirmDelete(true),
            disabled: isDeleting,
            destructive: true,
          },
        ]}
      />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('detail.delete')}</AlertDialogTitle>
            <AlertDialogDescription>{t('detail.deleteConfirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('info.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('detail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Identity block — no card, and no avatar: initials carry no information
          and cost the width a name needs at 390 (13 §2). */}
      <div className="mb-[19.2px]">
        <p className="text-sm text-surface-500">
          {client.zaloPhone ? (
            <a href={`tel:${client.zaloPhone.replace(/\s/g, '')}`} className="tabular-nums hover:underline">
              {t('detail.phoneZalo', { phone: client.zaloPhone })}
            </a>
          ) : (
            '—'
          )}
          {client.email && <span className="hidden sm:inline"> · {client.email}</span>}
        </p>
        <p className="mt-[3.2px] text-[13px] text-surface-400">
          {t('detail.added', { date: new Date(client.createdAt).toLocaleDateString('vi-VN') })}
        </p>
        <div className="mt-2">
          <Tags
            tags={client.tags}
            mode={canManage ? 'edit' : 'read'}
            onAdd={handleAddTag}
            onRemove={handleRemoveTag}
          />
        </div>
      </div>
    </>
  );
}
